import {
  createSalesTaxProduct,
  useSalesTaxProductTaxCodes,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  SingleCombobox,
  Textarea,
} from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import { TAX_CODE_REFERENCE_URL } from '../../common/constants';
import {
  addNewProductSchema,
  AddProductFormFields,
} from '../schema/add-product';

const SalesTaxAddNewProductModal = ({
  isProductsModalShown,
  setIsProductsModalShown,
  corporationId,
  setNotification,
  mutate,
  setIsAddingNewProduct,
}: {
  isProductsModalShown: boolean;
  setIsProductsModalShown: () => void;
  corporationId: number;
  setNotification: TSetNotification;
  mutate: KeyedMutator<unknown>;
  setIsAddingNewProduct: (isAddingNewProduct: boolean) => void;
}) => {
  const [productTaxCodes, setProductTaxCodes] = useState([]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setValue,
    clearErrors,
  } = useForm<AddProductFormFields>({
    resolver: zodResolver(addNewProductSchema),
  });

  const { data: taxCodes } = useSalesTaxProductTaxCodes({
    token: undefined,
    filters: { corporationId },
  });

  useEffect(() => {
    if (!taxCodes) return;

    setProductTaxCodes(Array.isArray(taxCodes) ? taxCodes : []);
  }, [taxCodes]);

  const onSubmit = async (formData: AddProductFormFields) => {
    setIsAddingNewProduct(true);
    const res = await createSalesTaxProduct(
      {
        sku: formData.sku,
        name: formData.name,
        description: formData.description,
        tax_code: formData.taxCode,
      },
      undefined,
      corporationId
    );

    if (!res.success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: res.error,
      });
      setIsProductsModalShown();
      setIsAddingNewProduct(false);
      return;
    }

    await mutate();
    reset();

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: `Product is successfully added.`,
      timeout: 10000,
    });

    setIsProductsModalShown();

    setTimeout(() => {
      setIsAddingNewProduct(false);
    }, 2000);
  };

  useEffect(() => {
    if (!isProductsModalShown) {
      reset();
    }
  }, [isProductsModalShown, reset]);

  return (
    <Dialog open={isProductsModalShown} onOpenChange={setIsProductsModalShown}>
      <DialogContent
        className="bg-white min-w-[600px] flex flex-col"
        preventCloseOnOutsideClick
        hideCloseIcon
      >
        <DialogHeader>
          <DialogTitle>Add new product</DialogTitle>
        </DialogHeader>

        <form
          className="flex flex-col flex-1 overflow-hidden"
          onSubmit={handleSubmit(onSubmit, (formError: unknown) => {
            console.error(formError);
          })}
        >
          <div className="flex-1 overflow-y-auto">
            <p className="txt-large-bold text-text-secondary py-5 px-6">
              Product information
            </p>
            <div className="flex flex-col gap-5 px-6">
              <FieldWrapper>
                <Label>
                  SKU<span className="text-bg-fg-secondary">*</span>
                </Label>
                <Input
                  id="sku"
                  {...register('sku')}
                  error={errors.sku?.message}
                  defaultValue={watch('sku')}
                  placeholder="Enter unique product ID"
                />
              </FieldWrapper>
              <FieldWrapper>
                <Label>
                  Product name<span className="text-bg-fg-secondary">*</span>
                </Label>
                <Input
                  id="name"
                  {...register('name')}
                  error={errors.name?.message}
                  defaultValue={watch('name')}
                  placeholder="Enter product name"
                />
              </FieldWrapper>
              <FieldWrapper>
                <Label>Product description</Label>
                <Textarea
                  {...register('description')}
                  value={watch('description')}
                  error={errors.description?.message}
                  placeholder="Enter a brief description of your product"
                />
              </FieldWrapper>
            </div>
            <p className="txt-large-bold text-text-secondary pt-8 pb-5 px-6">
              Tax information
            </p>
            <div className="flex flex-col gap-5 pb-10 px-6">
              <FieldWrapper>
                <Label>
                  Product tax code
                  <span className="text-bg-fg-secondary">*</span>
                </Label>

                <SingleCombobox
                  options={
                    productTaxCodes?.map((code) => ({
                      value: code.code,
                      label: code.code.replaceAll('_', ' '),
                    })) || []
                  }
                  value={watch('taxCode') || ''}
                  setValue={(val) => {
                    setValue('taxCode', val);
                    clearErrors('taxCode');
                  }}
                  placeholder="Select tax code"
                  error={errors?.taxCode?.message}
                  _className="txt-small-medium"
                  canClearSelection={false}
                />
                <p className="txt-small-bold text-text-tertiary cursor-pointer mt-1.5 ml-1">
                  <span
                    className="txt-small-bold text-utility-blue-600"
                    onClick={() =>
                      window.open(
                        TAX_CODE_REFERENCE_URL,
                        'TaxCodeReference',
                        'width=1000,height=800,left=200,top=200'
                      )
                    }
                  >
                    View our tax code database.
                  </span>
                </p>
              </FieldWrapper>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              size="sm"
              onClick={setIsProductsModalShown}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button size="sm" disabled={isSubmitting} type="submit">
              Add product
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SalesTaxAddNewProductModal;
